import React, { useState } from 'react';
import { Keyboard } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HStack } from '@/components/gluestack/hstack';
import { Input, InputField } from '@/components/gluestack/input';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from '@/components/gluestack/form-control';
import { Button, ButtonSpinner, ButtonText } from '@/components/gluestack/button';
import { AlertCircleIcon } from '@/components/gluestack/icon';
import { useToastMessages } from '@/hooks/useToastMessages';
import { BillingService, IRedeemGiftCertificateResponse } from '@/services/domain/BillingService';

type RedeemErrorCode = Exclude<IRedeemGiftCertificateResponse['Code'], 'SUCCESS'>;

const ERROR_KEYS: Record<RedeemErrorCode, string> = {
  INVALID_FORMAT: 'account.certificates.redeem.invalidFormat',
  NOT_FOUND: 'account.certificates.redeem.notFound',
  ALREADY_REDEEMED: 'account.certificates.redeem.alreadyRedeemed',
  TOO_MANY_ATTEMPTS: 'account.certificates.redeem.tooManyAttempts',
};

interface IRedeemGiftCertificateFormProps {
  /** The code field fills the row, with the button beside it (for a fixed-width column like the right panel) */
  fullWidth?: boolean;
}

export function RedeemGiftCertificateForm({ fullWidth = false }: IRedeemGiftCertificateFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [code, setCode] = useState('');
  const [errorCode, setErrorCode] = useState<RedeemErrorCode | null>(null);

  const redeemMutation = useMutation({
    mutationFn: () => BillingService.redeemGiftCertificate(code.trim()),
    onSuccess: (response) => {
      if (response.Code !== 'SUCCESS') {
        setErrorCode(response.Code);
        return;
      }
      setCode('');
      Keyboard.dismiss();
      showSuccessToast(t('account.certificates.redeem.success', { count: response.Days ?? 0 }));
      // Premium status feeds many screens, so refresh everything (same as a store purchase)
      void queryClient.invalidateQueries();
    },
    onError: (error) => showErrorToast(t('account.certificates.redeem.error'), error),
  });

  const handleRedeem = () => {
    if (!code.trim() || redeemMutation.isPending) return;
    setErrorCode(null);
    redeemMutation.mutate();
  };

  return (
    <>
      <FormControl isInvalid={!!errorCode}>
        <HStack className={`items-center gap-2 ${fullWidth ? '' : 'flex-wrap'}`}>
          <Input className={fullWidth ? 'min-w-0 flex-1' : 'w-80 max-w-full'}>
            <InputField
              placeholder={t('account.certificates.redeem.placeholder')}
              aria-label={t('account.certificates.redeem.title')}
              value={code}
              onChangeText={(value) => {
                setCode(value);
                setErrorCode(null);
              }}
              autoCapitalize="characters"
              autoCorrect={false}
              autoComplete="off"
              returnKeyType="done"
              onSubmitEditing={handleRedeem}
              className="font-mono"
            />
          </Input>
          <Button
            size="sm"
            onPress={handleRedeem}
            isDisabled={!code.trim() || redeemMutation.isPending}>
            {redeemMutation.isPending && <ButtonSpinner className="mr-2" />}
            <ButtonText>{t('account.certificates.redeem.button')}</ButtonText>
          </Button>
        </HStack>
        {errorCode ? (
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText className="flex-1">
              {t(ERROR_KEYS[errorCode] ?? 'account.certificates.redeem.error')}
            </FormControlErrorText>
          </FormControlError>
        ) : (
          <FormControlHelper>
            <FormControlHelperText>{t('account.certificates.redeem.helper')}</FormControlHelperText>
          </FormControlHelper>
        )}
      </FormControl>
    </>
  );
}
